import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ClockCounterClockwise, DownloadSimple, ArrowRight, ArrowLeft, UploadSimple,
  CheckCircle, WarningCircle, XCircle,
} from "@phosphor-icons/react";

const rel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

export default function ImportHistory() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/import/history").then((r) => setItems(r.data));
  }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/import/history/${id}`);
    setSelected(data);
  };

  const downloadReport = async (id) => {
    try {
      const res = await api.get(`/import/history/${id}/report.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import_report_${id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (_) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <Link to="/import" data-testid="history-back-link" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to Import
      </Link>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-label text-orange-400 mb-2">AUDIT LOG</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Import History</h1>
          <p className="text-slate-400 mt-2">Every CSV import, who did it, when, and what happened.</p>
        </div>
        <Button
          onClick={() => navigate("/import")}
          className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-5"
        >
          <UploadSimple size={16} weight="bold" className="mr-2" /> New Import
        </Button>
      </div>

      {!items ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-16 text-center" data-testid="history-empty">
          <ClockCounterClockwise size={44} weight="duotone" className="text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-semibold">No imports yet</div>
          <div className="text-slate-500 text-sm mt-1">Run your first CSV import to see the audit trail here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-slate-900 overflow-hidden">
            <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_1fr_60px] font-label text-slate-500 px-6 py-3 border-b border-white/10">
              <div>FILE</div>
              <div>USER</div>
              <div>WHEN</div>
              <div>OUTCOME</div>
              <div></div>
            </div>
            <div className="divide-y divide-white/5">
              {items.map((it) => (
                <button
                  key={it.id}
                  data-testid={`history-row-${it.id}`}
                  onClick={() => openDetail(it.id)}
                  className={`w-full text-left grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr_60px] gap-3 px-6 py-4 hover:bg-white/[0.02] transition-colors items-center ${selected?.id === it.id ? "bg-orange-500/5" : ""}`}
                >
                  <div className="col-span-2 md:col-span-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{it.filename || "import.csv"}</div>
                    <div className="text-xs text-slate-500">
                      {it.created_credentials} credentials · {it.created_staff} new staff
                    </div>
                  </div>
                  <div className="text-sm text-slate-300 truncate">{it.user_name}</div>
                  <div className="text-sm text-slate-400" title={new Date(it.imported_at).toLocaleString()}>
                    {rel(it.imported_at)}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px]">
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5">
                      <CheckCircle size={10} weight="bold" /> {it.counts?.valid || 0}
                    </span>
                    {it.counts?.duplicate > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/25 bg-amber-500/10 text-amber-300 px-1.5 py-0.5">
                        <WarningCircle size={10} weight="bold" /> {it.counts.duplicate}
                      </span>
                    )}
                    {it.counts?.error > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/25 bg-rose-500/10 text-rose-300 px-1.5 py-0.5">
                        <XCircle size={10} weight="bold" /> {it.counts.error}
                      </span>
                    )}
                  </div>
                  <ArrowRight size={14} weight="bold" className="hidden md:block text-slate-500 justify-self-end" />
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 sticky top-6 max-h-[80vh] overflow-y-auto">
            {!selected ? (
              <div className="text-slate-500 text-sm">Select an import to view details.</div>
            ) : (
              <div data-testid="history-detail">
                <div className="font-label text-slate-500 mb-1">IMPORT DETAIL</div>
                <div className="font-display font-black text-lg tracking-tight break-all">{selected.filename}</div>
                <div className="text-xs text-slate-500 mt-1">
                  By {selected.user_name} · {new Date(selected.imported_at).toLocaleString()}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-5">
                  <div className="rounded-lg bg-slate-950 border border-white/10 p-3 text-center">
                    <div className="text-xl font-display font-black text-emerald-300">{selected.counts?.valid || 0}</div>
                    <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Valid</div>
                  </div>
                  <div className="rounded-lg bg-slate-950 border border-white/10 p-3 text-center">
                    <div className="text-xl font-display font-black text-amber-300">{selected.counts?.duplicate || 0}</div>
                    <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Duplicate</div>
                  </div>
                  <div className="rounded-lg bg-slate-950 border border-white/10 p-3 text-center">
                    <div className="text-xl font-display font-black text-rose-300">{selected.counts?.error || 0}</div>
                    <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Error</div>
                  </div>
                </div>

                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400"><span>New staff created</span><span className="text-slate-100 font-semibold">{selected.created_staff}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Credentials created</span><span className="text-slate-100 font-semibold">{selected.created_credentials}</span></div>
                </div>

                <Button
                  data-testid="download-import-report"
                  onClick={() => downloadReport(selected.id)}
                  className="mt-5 w-full bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
                >
                  <DownloadSimple size={14} weight="bold" className="mr-2" /> Download Report CSV
                </Button>

                {selected.error_rows?.length > 0 && (
                  <div className="mt-5">
                    <div className="font-label text-rose-400 mb-2">ERROR ROWS ({selected.error_rows.length})</div>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-2">
                      {selected.error_rows.slice(0, 30).map((r, i) => (
                        <div key={i} className="rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2 text-xs">
                          <div className="text-slate-200">Row {r.row}: {r.first_name} {r.last_name}</div>
                          <div className="text-rose-300 mt-1">{r.errors?.join("; ")}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.duplicate_rows?.length > 0 && (
                  <div className="mt-5">
                    <div className="font-label text-amber-400 mb-2">DUPLICATE ROWS ({selected.duplicate_rows.length})</div>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                      {selected.duplicate_rows.slice(0, 30).map((r, i) => (
                        <div key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs">
                          <div className="text-slate-200">Row {r.row}: {r.first_name} {r.last_name}</div>
                          <div className="text-amber-300 mt-1">{r.credential_type} @ {r.expiration_date}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
